import { useState } from 'react';
import { Bot, ChevronDown, Trophy } from 'lucide-react';
import type { Competition } from '../../data/competitions';
import { analyze, verdict } from '../../engine/coach';
import { loadJSON, storageKeys } from '../../storage';
import type { RobotValues, SimResult } from '../../types';

interface Props {
  comp: Competition;
  robot: RobotValues;
  result: SimResult | null;
  onGoTo: (tab: string) => void;
}

const EVENT_DOT: Record<SimResult['events'][number]['type'], string> = {
  info: 'bg-slate-400',
  good: 'bg-emerald-400',
  bad: 'bg-rose-400',
  warn: 'bg-amber-400',
};

function SpeedGraph({ series, accent }: { series: number[]; accent: string }) {
  if (series.length < 2) return null;
  const max = Math.max(0.01, ...series);
  const w = 100;
  const h = 32;
  const pts = series
    .map((v, i) => `${(i / (series.length - 1)) * w},${h - (v / max) * h}`)
    .join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="h-16 w-full">
      <polyline points={pts} fill="none" stroke="currentColor" strokeWidth={1.2} className={accent} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

export default function ImproveTab({ comp, robot, result, onGoTo }: Props) {
  const [faqOpen, setFaqOpen] = useState<number | null>(null);
  const best = loadJSON<number | null>(storageKeys.bestScore(comp.id), null);

  if (!result) {
    return (
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-8 text-center lg:col-span-2">
          <Bot className={`mx-auto size-10 ${comp.text}`} />
          <h3 className="mt-3 text-lg font-extrabold text-white">No run yet</h3>
          <p className="mx-auto mt-2 max-w-md text-sm text-slate-400">
            Run a simulation first — Taa Bot will analyze your robot, the rule pack, and the replay to suggest
            specific tuning changes.
          </p>
          <button
            type="button"
            onClick={() => onGoTo('Simulate')}
            className={`mt-5 inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-bold ${comp.button}`}
          >
            Go to Simulate
          </button>
        </div>
        <FaqPanel comp={comp} faqOpen={faqOpen} setFaqOpen={setFaqOpen} />
      </div>
    );
  }

  const tips = analyze(result, robot);

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className="space-y-5 lg:col-span-2">
        {/* Result banner */}
        <div
          className={`rounded-2xl border p-5 ${
            result.won ? 'border-emerald-400/30 bg-emerald-400/5' : 'border-rose-400/30 bg-rose-400/5'
          }`}
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className={`text-lg font-extrabold ${result.won ? 'text-emerald-300' : 'text-rose-300'}`}>
              {result.outcome}
            </h3>
            <span className="text-sm font-bold text-slate-300">Score: {result.score} pts</span>
          </div>
          <p className="mt-1 text-sm text-slate-400">{verdict(result)}</p>
        </div>

        {/* Taa Bot suggestions */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
          <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-400">
            <Bot className="size-4" />
            Taa Bot suggestions
          </h3>
          <ul className="mt-3 space-y-3">
            {tips.map((tip, i) => (
              <li key={i} className="flex gap-2.5 rounded-xl bg-white/[0.03] p-3 text-sm leading-relaxed text-slate-300">
                <span className={`mt-0.5 shrink-0 font-bold ${comp.text}`}>{i + 1}.</span>
                {tip}
              </li>
            ))}
          </ul>
        </div>

        {/* Replay / metrics */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
          <h3 className="text-sm font-bold uppercase tracking-wide text-slate-400">Performance report</h3>
          <dl className="mt-3 grid gap-2 sm:grid-cols-2">
            {Object.entries(result.metrics).map(([k, v]) => (
              <div key={k} className="flex justify-between gap-3 rounded-lg bg-white/[0.03] px-3 py-2 text-sm">
                <dt className="text-slate-400">{k}</dt>
                <dd className="font-mono font-semibold text-slate-200">{v}</dd>
              </div>
            ))}
          </dl>

          {result.speedSeries.length > 1 && (
            <div className="mt-4">
              <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500">Speed over time</h4>
              <SpeedGraph series={result.speedSeries} accent={comp.text} />
            </div>
          )}

          {result.events.length > 0 && (
            <div className="mt-4">
              <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500">Event timeline</h4>
              <ul className="mt-2 max-h-48 space-y-1.5 overflow-y-auto pe-1 text-xs">
                {result.events.map((e, i) => (
                  <li key={i} className="flex items-start gap-2 text-slate-400">
                    <span className={`mt-1 size-1.5 shrink-0 rounded-full ${EVENT_DOT[e.type]}`} />
                    <span className="tabular-nums text-slate-500">{e.t.toFixed(1)}s</span>
                    {e.msg}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-5">
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
          <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-400">
            <Trophy className="size-4 text-amber-300" />
            Leaderboard
          </h3>
          <div className="mt-3 flex items-center justify-between rounded-xl bg-white/[0.03] px-3 py-2.5">
            <span className="text-sm text-slate-400">Your best score</span>
            <span className="text-lg font-extrabold text-amber-300">{best ?? '—'} pts</span>
          </div>
          <div className="mt-2 flex items-center justify-between rounded-xl bg-white/[0.03] px-3 py-2.5">
            <span className="text-sm text-slate-400">This run</span>
            <span className="text-lg font-extrabold text-slate-200">{result.score} pts</span>
          </div>
          <button
            type="button"
            onClick={() => onGoTo('Build')}
            className={`mt-4 w-full rounded-xl px-4 py-2.5 text-sm font-bold ${comp.button}`}
          >
            Tune robot &amp; re-run
          </button>
        </div>

        <FaqPanel comp={comp} faqOpen={faqOpen} setFaqOpen={setFaqOpen} />
      </div>
    </div>
  );
}

function FaqPanel({
  comp,
  faqOpen,
  setFaqOpen,
}: {
  comp: Competition;
  faqOpen: number | null;
  setFaqOpen: (i: number | null) => void;
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
      <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-400">
        <Bot className="size-4" />
        Ask Taa Bot
      </h3>
      <div className="mt-3 space-y-1.5">
        {comp.faq.map((item, i) => (
          <div key={i} className="rounded-xl bg-white/[0.03]">
            <button
              type="button"
              onClick={() => setFaqOpen(faqOpen === i ? null : i)}
              className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left text-sm font-semibold text-slate-200"
            >
              {item.q}
              <ChevronDown className={`size-4 shrink-0 text-slate-500 transition-transform ${faqOpen === i ? 'rotate-180' : ''}`} />
            </button>
            {faqOpen === i && (
              <p className={`px-3 pb-3 text-sm leading-relaxed text-slate-400`}>{item.a}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
